import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import BASE_URL from '../services/api';
import DROPOUT_METRICS from '../data/dropoutMetrics';
import SCHOOL_RESULTS from '../data/schoolResults';
import { ADW_SCHOOLS, TW_SCHOOLS } from '../data/schoolMetrics';
import FRA_DISTRICT_CODES from '../data/fraDistrictCodes';
import LAND_PATTAS_DISTRICT from '../data/landPattasDistrict';
import canonicalDistrict, { CANONICAL_DISTRICTS } from '../data/districtCanonicalizer';

const FRA_API  = `${BASE_URL}/dashboard/fra`;
const TIME_API = `${BASE_URL}/dashboard/time`;

const DistrictRanking = () => {
  const navigate = useNavigate();
  const [fraByDistrict, setFraByDistrict] = useState({});
  const [timeData, setTimeData] = useState([]);
  const [loading, setLoading] = useState(true);
  // Metric filter - empty array = composite across ALL metrics; otherwise
  // ranking is computed across the selected subset only. When exactly one
  // metric is selected the UI shows that single metric's value column.
  const [selectedMetrics, setSelectedMetrics] = useState([]);
  // District filter - empty array = show all; otherwise show only selected.
  const [selectedDistricts, setSelectedDistricts] = useState([]);
  // Tier filter - '' = all districts, otherwise 'green' | 'amber' | 'red'
  const [selectedTier, setSelectedTier] = useState('');
  // Backward-compat helper: many places used selectedMetric (single).
  const selectedMetric = selectedMetrics.length === 1 ? selectedMetrics[0] : '';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const entries = Object.entries(FRA_DISTRICT_CODES);
        const BATCH = 4;
        const REQ_TIMEOUT_MS = 8000;
        const fetchOne = async ([name, code]) => {
          const ctrl = new AbortController();
          const timer = setTimeout(() => ctrl.abort(), REQ_TIMEOUT_MS);
          try {
            const res = await fetch(`${FRA_API}?typeid=3007&input01=${code}`, { signal: ctrl.signal });
            if (!res.ok) return [name, null];
            const json = await res.json();
            const row = Array.isArray(json.data) && json.data[0] ? json.data[0] : null;
            return [name, row];
          } catch { return [name, null]; }
          finally { clearTimeout(timer); }
        };
        const fraResults = [];
        for (let i = 0; i < entries.length; i += BATCH) {
          const chunk = entries.slice(i, i + BATCH);
          const results = await Promise.all(chunk.map(fetchOne));
          fraResults.push(...results);
        }
        const timeRes = await fetch(TIME_API, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ division:[], district:[], MainCategory:[], Subcategory:[],
                                 contractorId:'', departmentIds:[], year:[],
                                 selectionType:'string', costOrCount:'string' }),
        });
        const timeJson = await timeRes.json();
        if (cancelled) return;
        const map = {};
        fraResults.forEach(([name, row]) => {
          const canon = canonicalDistrict(name);
          if (row && canon) map[canon] = row;
        });
        setFraByDistrict(map);
        setTimeData(timeJson?.data?.data || []);
      } catch { /* silent */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  // Metric config
  const METRICS = useMemo(() => {
    // Build per-metric district → value maps. Every district name goes through
    // canonicalDistrict() so spelling drift across sources gets collapsed.
    const toCanonMap = (obj, valueGetter = (v) => v) => {
      const out = {};
      Object.entries(obj || {}).forEach(([k, v]) => {
        const canon = canonicalDistrict(k);
        if (!canon) return;
        const val = valueGetter(v);
        if (val == null) return;
        out[canon] = val;
      });
      return out;
    };
    // TIME - only the pink cell 'Not Started' is used, aggregated per district.
    const timeNotStarted = {};
    timeData.forEach(r => {
      const canon = canonicalDistrict(r.districtName);
      if (!canon) return;
      timeNotStarted[canon] = (timeNotStarted[canon] || 0) + (r.notStarted || 0);
    });
    const fraGet = (key) => {
      const out = {};
      Object.entries(fraByDistrict).forEach(([d, row]) => {
        if (row?.[key] != null) out[d] = row[key];
      });
      return out;
    };
    // 69 metrics - the final ranking set (mirrors what shows on Integrated Dashboard).
    // Live where we have the source wired; empty map = awaits manual entry.
    return [
      // --- Section 4 - School Admission (ADW) ------------------------
      { key: '10_adw_pass',    label: 'Section 4 - Class X Pass % (2025-26) - ADW Total',  higher: true, unit: '%', map: toCanonMap(SCHOOL_RESULTS['10_adw'].districts, v => v?.pass_pct) },
      { key: '12_adw_pass',    label: 'Section 4 - Class XII Pass % (2025-26) - ADW Total', higher: true, unit: '%', map: toCanonMap(SCHOOL_RESULTS['12_adw'].districts, v => v?.pass_pct) },

      // --- Section 4 - School Admission (TW) -------------------------
      { key: '10_tw_pass',     label: 'Section 4 - Class X Pass % (2025-26) - TW Total',   higher: true, unit: '%', map: toCanonMap(SCHOOL_RESULTS['10_tw'].districts, v => v?.pass_pct) },
      { key: '12_tw_pass',     label: 'Section 4 - Class XII Pass % (2025-26) - TW Total', higher: true, unit: '%', map: toCanonMap(SCHOOL_RESULTS['12_tw'].districts, v => v?.pass_pct) },

      // --- Section 5 - Hostel (ADW) ----------------------------------
      { key: 'h5_online_dev',   label: 'Section 5 - ADW Hostel - Number of Online Devices (number & %)', higher: true,  map: {} },
      { key: 'h5_avg_att',      label: 'Section 5 - ADW Hostel - Average Daily Attendance %',            higher: true,  unit: '%', map: {} },
      { key: 'h5_below75',      label: 'Section 5 - ADW Hostel - Students with Attendance below 75% (%)',higher: false, unit: '%', map: {} },
      { key: 'h5_dadwo_pend',   label: 'Section 5 - ADW Hostel - DADWO Inspections Pending (number & %)',higher: false, map: {} },
      { key: 'h5_st_pend',      label: 'Section 5 - ADW Hostel - ST Inspections Pending (number & %)',   higher: false, map: {} },

      // --- Section 5(a) - Hostel (TW) --------------------------------
      { key: 'h5a_online_dev',  label: 'Section 5(a) - TW Hostel - Number of Online Devices (number & %)',higher: true,  map: {} },
      { key: 'h5a_avg_att',     label: 'Section 5(a) - TW Hostel - Average Daily Attendance %',           higher: true,  unit: '%', map: {} },
      { key: 'h5a_below75',     label: 'Section 5(a) - TW Hostel - Students with Attendance below 75% (%)',higher: false, unit: '%', map: {} },
      { key: 'h5a_dadwo_pend',  label: 'Section 5(a) - TW Hostel - DADWO Inspections Pending (number & %)',higher: false, map: {} },
      { key: 'h5a_st_pend',     label: 'Section 5(a) - TW Hostel - ST Inspections Pending (number & %)',   higher: false, map: {} },

      // --- Section 6 - Land Pattas -----------------------------------
      { key: 'p6_yet_survey',   label: 'Section 6 - Land - Fields yet to be Surveyed',              higher: false, map: {} },
      { key: 'p6_yet_adnatham', label: 'Section 6 - Land - Yet to be converted to AD Natham',        higher: false, map: {} },
      { key: 'eligible_np',     label: 'Section 6 - Land - Eligible but not yet Patta given',        higher: false, map: toCanonMap(LAND_PATTAS_DISTRICT.districts, v => v?.eligible_not_given) },
      { key: 'not_scrut',       label: 'Section 6 - Land - Applications not yet Scrutinised',        higher: false, map: toCanonMap(LAND_PATTAS_DISTRICT.districts, v => v?.not_scrutinised) },
      { key: 'p6_epatta_yet',   label: 'Section 6 - Land - e-Pattas yet to be Issued',               higher: false, map: {} },

      // --- Section 7 - Court Cases -----------------------------------
      { key: 'court_pending',   label: 'Section 7 - Court Cases - Pending',                          higher: false, map: {} },

      // --- Section 8 - CM Cell Petitions -----------------------------
      { key: 'cm_pending',      label: 'Section 8 - CM Cell Petitions - Total Pending',              higher: false, map: {} },

      // --- Section 9 - RTI -------------------------------------------
      { key: 'rti_pending',     label: 'Section 9 - RTI - Pending',                                  higher: false, map: {} },

      // --- Section 11.1 - SC Habitations -----------------------------
      { key: 'burial_sc_nodedic', label: 'Section 11.1 - SC Habitations without dedicated Burial Grounds', higher: false, map: {} },
      { key: 'burial_sc_nopath',  label: 'Section 11.1 - SC Habitations without Pathways',                  higher: false, map: {} },

      // --- Section 11.2 - ST Habitations -----------------------------
      { key: 'burial_st_nodedic', label: 'Section 11.2 - ST Habitations without dedicated Burial Grounds', higher: false, map: {} },
      { key: 'burial_st_nopath',  label: 'Section 11.2 - ST Habitations without Pathways',                  higher: false, map: {} },

      // --- Section 12 - Transport / Water ----------------------------
      { key: 'no_transport',    label: 'Section 12 - Habitations without Public Transport Access',   higher: false, map: {} },
      { key: 'no_roads',        label: 'Section 12 - Habitations without All-Weather Roads',          higher: false, map: {} },
      { key: 'no_water',        label: 'Section 12 - Habitations without Drinking Water',             higher: false, map: {} },
      { key: 'no_mobile',       label: 'Section 12 - Habitations without Mobile Network Access',      higher: false, map: {} },
      { key: 'no_electricity',  label: 'Section 12 - Habitations without Electricity',                higher: false, map: {} },
      { key: 'no_elec_st',      label: 'Section 12 - ST Households without Electricity',              higher: false, map: {} },

      // --- Section 14 - DAPSC/ST - DMC Meetings ----------------------
      { key: 'dmc_q1',          label: 'Section 14 - DMC Meeting Status - Quarter 1 (Conducted / Not Conducted)', higher: true, map: {} },
      { key: 'dmc_q2',          label: 'Section 14 - DMC Meeting Status - Quarter 2 (Conducted / Not Conducted)', higher: true, map: {} },
      { key: 'dmc_q3',          label: 'Section 14 - DMC Meeting Status - Quarter 3 (Conducted / Not Conducted)', higher: true, map: {} },
      { key: 'dmc_q4',          label: 'Section 14 - DMC Meeting Status - Quarter 4 (Conducted / Not Conducted)', higher: true, map: {} },

      // --- Section 15 - Community Certificate ------------------------
      { key: 'cc_pending',      label: 'Section 15 - Community Certificate - Applications Pending',   higher: false, map: {} },

      // --- Section 18A - DLVMC --------------------------------------
      { key: 'dlvmc_q1',        label: 'Section 18A - DLVMC Status - Quarter 1 (Conducted / Not Conducted)', higher: true, map: {} },
      { key: 'dlvmc_q2',        label: 'Section 18A - DLVMC Status - Quarter 2 (Conducted / Not Conducted)', higher: true, map: {} },
      { key: 'dlvmc_q3',        label: 'Section 18A - DLVMC Status - Quarter 3 (Conducted / Not Conducted)', higher: true, map: {} },
      { key: 'dlvmc_q4',        label: 'Section 18A - DLVMC Status - Quarter 4 (Conducted / Not Conducted)', higher: true, map: {} },

      // --- Section 18B - SDLVMC -------------------------------------
      { key: 'sdlvmc_q1',       label: 'Section 18B - SDLVMC Status - Quarter 1 (Conducted / Not Conducted)', higher: true, map: {} },
      { key: 'sdlvmc_q2',       label: 'Section 18B - SDLVMC Status - Quarter 2 (Conducted / Not Conducted)', higher: true, map: {} },
      { key: 'sdlvmc_q3',       label: 'Section 18B - SDLVMC Status - Quarter 3 (Conducted / Not Conducted)', higher: true, map: {} },
      { key: 'sdlvmc_q4',       label: 'Section 18B - SDLVMC Status - Quarter 4 (Conducted / Not Conducted)', higher: true, map: {} },

      // --- Section 19 - POA Claims ----------------------------------
      { key: 'poa_mr_s1',       label: 'Section 19 - POA - Cases pending Monetary Relief (Stage 1)', higher: false, map: {} },
      { key: 'poa_mr_s2',       label: 'Section 19 - POA - Cases pending Monetary Relief (Stage 2)', higher: false, map: {} },
      { key: 'poa_mr_s3',       label: 'Section 19 - POA - Cases pending Monetary Relief (Stage 3)', higher: false, map: {} },
      { key: 'poa_ar_job',      label: 'Section 19 - POA - Cases pending Additional Relief (Job)',   higher: false, map: {} },
      { key: 'poa_ar_pension',  label: 'Section 19 - POA - Cases pending Additional Relief (Pension)', higher: false, map: {} },
      { key: 'poa_ar_patta',    label: 'Section 19 - POA - Cases pending Additional Relief (Patta)', higher: false, map: {} },

      // --- Section 20 - Loan Applications (Pending) ------------------
      { key: 'loan_vetri',      label: 'Section 20 - Loan - Vetri Thozhil Munaivor Thittam (Pending)',     higher: false, map: {} },
      { key: 'loan_pmajay',     label: 'Section 20 - Loan - PM-AJAY Subsidy (Pending)',                    higher: false, map: {} },
      { key: 'loan_mannin',     label: 'Section 20 - Loan - Mannin Magal Thittam (Pending)',               higher: false, map: {} },
      { key: 'loan_nskfdc',     label: 'Section 20 - Loan - Education Loan NSKFDC (Safai Karmachari) (Pending)', higher: false, map: {} },
      { key: 'loan_nstfdc',     label: 'Section 20 - Loan - Education Loan NSTFDC (ST) (Pending)',         higher: false, map: {} },
      { key: 'loan_nsfdc',      label: 'Section 20 - Loan - Education Loan NSFDC (SC) (Pending)',          higher: false, map: {} },
      { key: 'loan_micro',      label: 'Section 20 - Loan - Micro Credit Finance (Pending)',               higher: false, map: {} },

      // --- Section 21 - Construction Status --------------------------
      { key: 'con_hostel_uc',   label: 'Section 21 - Construction - Hostel - Under Construction',    higher: false, map: {} },
      { key: 'con_hostel_ns',   label: 'Section 21 - Construction - Hostel - Not Yet Started',        higher: false, map: {} },
      { key: 'con_school_uc',   label: 'Section 21 - Construction - School - Under Construction',    higher: false, map: {} },
      { key: 'con_school_ns',   label: 'Section 21 - Construction - School - Not Yet Started',        higher: false, map: {} },
      { key: 'con_chall_uc',    label: 'Section 21 - Construction - Community Hall - Under Construction', higher: false, map: {} },
      { key: 'con_chall_ns',    label: 'Section 21 - Construction - Community Hall - Not Yet Started',    higher: false, map: {} },

      // --- Section 22 - FRA (Forest Rights Act) ----------------------------
      { key: 'fra_frc',         label: 'Section 22 - FRA - FRC Verification Pending',                       higher: false, map: fraGet('FRC_VERIFICATION_PENDING') },
      { key: 'fra_pen_res',     label: 'Section 22 - FRA - Claims pending for Resolution (to SDLC)',        higher: false, map: fraGet('CLAIMS_PENDING_RESOLUTION') },
      { key: 'fra_pen_sdlc',    label: 'Section 22 - FRA - Claims Pending with SDLC',                       higher: false, map: fraGet('CLAIMS_PENDING_SDLC') },
      { key: 'fra_pen_dlc',     label: 'Section 22 - FRA - Claims Pending with DLC',                        higher: false, map: fraGet('CLAIMS_PENDING_DLC') },
      { key: 'fra_t_pending',   label: 'Section 22 - FRA - Titles Pending',                                 higher: false, map: fraGet('TITLES_PENDING') },
    ];
  }, [fraByDistrict, timeData]);

  // For each metric compute ordinal rank per district (1 = best).
  // Composite = sum of ranks across the ACTIVE metric subset (all metrics when
  // none selected). District filter restricts the visible rows but the ranking
  // itself is still computed across all 38 so ranks stay meaningful.
  const rankingRows = useMemo(() => {
    // Active metric subset — empty means "all metrics"
    const activeMetrics = selectedMetrics.length ? METRICS.filter(m => selectedMetrics.includes(m.key)) : METRICS;

    // Collect all canonical district names that appear in any active metric
    const allDistricts = new Set();
    activeMetrics.forEach(m => Object.keys(m.map).forEach(d => allDistricts.add(d)));
    const perDistrict = {};
    allDistricts.forEach(d => { perDistrict[d] = { ranks: {}, values: {} }; });
    activeMetrics.forEach(m => {
      const entries = Object.entries(m.map).filter(([, v]) => Number.isFinite(Number(v)));
      if (entries.length < 3) return;
      entries.sort((a, b) => m.higher ? Number(b[1]) - Number(a[1]) : Number(a[1]) - Number(b[1]));
      entries.forEach(([d, v], i) => {
        perDistrict[d].ranks[m.key]  = i + 1;
        perDistrict[d].values[m.key] = Number(v);
      });
    });

    // Single-metric case = show the raw value column, ranked 1..N.
    if (activeMetrics.length === 1) {
      const m = activeMetrics[0];
      const rows = Object.entries(perDistrict)
        .filter(([, s]) => s.ranks[m.key] != null)
        .map(([d, s]) => ({ district: d, rank: s.ranks[m.key], value: s.values[m.key], metricsCounted: 1, totalMetrics: 1 }))
        .sort((a, b) => a.rank - b.rank);
      rows.forEach((r, i) => { r.rank = i + 1; });
      return selectedDistricts.length ? rows.filter(r => selectedDistricts.includes(r.district)) : rows;
    }

    // Composite = sum of ranks across active metrics (lower = better).
    const worstRankByMetric = {};
    activeMetrics.forEach(m => {
      const count = Object.values(m.map).filter(v => Number.isFinite(Number(v))).length;
      worstRankByMetric[m.key] = count > 0 ? count + 1 : 0;
    });
    const rows = Object.entries(perDistrict).map(([d, s]) => {
      let sum = 0, counted = 0;
      activeMetrics.forEach(m => {
        if (s.ranks[m.key] != null) { sum += s.ranks[m.key]; counted += 1; }
        else sum += worstRankByMetric[m.key] || 0;
      });
      return { district: d, rankSum: sum, metricsCounted: counted, totalMetrics: activeMetrics.length, perMetricRanks: s.ranks, perMetricValues: s.values };
    });
    rows.sort((a, b) => a.rankSum - b.rankSum);
    rows.forEach((r, i) => { r.rank = i + 1; });
    return selectedDistricts.length ? rows.filter(r => selectedDistricts.includes(r.district)) : rows;
  }, [METRICS, selectedMetrics, selectedDistricts]);

  const of = rankingRows.length;
  const cutTop = Math.ceil(of / 3);
  const cutBot = of - cutTop;
  const tierOf = (idx) => idx < cutTop ? 'green' : idx >= cutBot ? 'red' : 'amber';
  const gridTone = {
    green: 'bg-emerald-50 border-emerald-300 hover:bg-emerald-100',
    amber: 'bg-amber-50 border-amber-300 hover:bg-amber-100',
    red:   'bg-rose-50 border-rose-300 hover:bg-rose-100',
  };
  const gridBadge = {
    green: 'bg-emerald-500 text-white',
    amber: 'bg-amber-500 text-white',
    red:   'bg-rose-500 text-white',
  };
  const goToDistrict = (name) => navigate(`/integrated-dashboard?district=${encodeURIComponent(name)}`);
  const fmtVal = (v, unit) => unit === '%' ? `${Number(v).toFixed(2)}%` : new Intl.NumberFormat('en-IN').format(v);

  const currentMetric = selectedMetric ? METRICS.find(m => m.key === selectedMetric) : null;

  return (
    <div className="p-4 space-y-3">
      {/* Section header */}
      <div className="bg-white border border-slate-200 shadow-sm">
        <div className="px-4 py-3 bg-[#0f2e4d] text-white text-base font-semibold uppercase tracking-wide flex items-center gap-2 flex-wrap">
          🏆 District Ranking
          <span className="text-[11px] font-normal normal-case text-blue-200">
            {selectedMetric
              ? `Ranked by "${currentMetric?.label}" — ${currentMetric?.higher ? 'higher is better' : 'lower is better'}`
              : selectedMetrics.length > 1
                ? `Composite across ${selectedMetrics.length} selected metrics. Lowest sum = best.`
                : `Composite ranking across ${METRICS.length} metrics. Lowest sum = best overall.`}
          </span>
        </div>
        {/* Filters — multi-select metrics + multi-select districts */}
        <div className="px-4 py-2.5 bg-slate-50 border-t border-slate-200 space-y-2">
          {/* Metric multi-select */}
          <div className="flex items-start gap-2 flex-wrap">
            <label className="text-[11px] uppercase tracking-wide text-slate-600 font-semibold pt-1 shrink-0">Rank by:</label>
            <div className="flex-1 min-w-[240px]">
              <select
                multiple
                value={selectedMetrics}
                onChange={(e) => setSelectedMetrics(Array.from(e.target.selectedOptions, o => o.value))}
                className="w-full px-2 py-1 border border-slate-300 text-sm bg-white focus:outline-none focus:border-[#0f2e4d]"
                size={Math.min(6, METRICS.length)}
              >
                {METRICS.map(m => (
                  <option key={m.key} value={m.key}>
                    {m.label} — {m.higher ? '↑ higher is better' : '↓ lower is better'}
                  </option>
                ))}
              </select>
              <p className="text-[10px] text-slate-500 mt-1">
                {selectedMetrics.length === 0 ? 'No selection = composite across ALL metrics.' :
                 selectedMetrics.length === 1 ? 'Single metric selected — ranked by that metric\'s value.' :
                 `${selectedMetrics.length} metrics selected — composite across this subset.`}
                {' '}Hold Ctrl/Cmd (or Shift) to select multiple.
              </p>
            </div>
            {selectedMetrics.length > 0 && (
              <button onClick={() => setSelectedMetrics([])} className="text-xs text-rose-700 hover:text-rose-900 font-semibold pt-1">Clear metrics</button>
            )}
          </div>

          {/* District multi-select */}
          <div className="flex items-start gap-2 flex-wrap">
            <label className="text-[11px] uppercase tracking-wide text-slate-600 font-semibold pt-1 shrink-0">Districts:</label>
            <div className="flex-1 min-w-[240px]">
              <select
                multiple
                value={selectedDistricts}
                onChange={(e) => setSelectedDistricts(Array.from(e.target.selectedOptions, o => o.value))}
                className="w-full px-2 py-1 border border-slate-300 text-sm bg-white focus:outline-none focus:border-[#0f2e4d]"
                size={Math.min(6, 8)}
              >
                {CANONICAL_DISTRICTS && CANONICAL_DISTRICTS.map(d => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
              <p className="text-[10px] text-slate-500 mt-1">
                {selectedDistricts.length === 0 ? 'No selection = all districts.' : `${selectedDistricts.length} district${selectedDistricts.length === 1 ? '' : 's'} selected.`}
              </p>
            </div>
            {selectedDistricts.length > 0 && (
              <button onClick={() => setSelectedDistricts([])} className="text-xs text-rose-700 hover:text-rose-900 font-semibold pt-1">Clear districts</button>
            )}
          </div>
        </div>
      </div>

      {loading && (
        <div className="bg-white border border-slate-200 p-6 text-center text-sm text-gray-500">Loading district data…</div>
      )}

      {/* Empty-state - this metric has no data yet (e.g. manual entry not filled) */}
      {!loading && of === 0 && selectedMetric && (
        <div className="bg-amber-50 border border-amber-200 p-6 text-center text-sm text-amber-800">
          No district data yet for <strong>{currentMetric?.label}</strong>. This metric will populate once districts start entering values.
        </div>
      )}

      {/* Grid - every district as a clickable card */}
      {of > 0 && (
        <div className="bg-white border border-slate-200">
          <div className="px-4 py-2.5 border-b border-slate-200 flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-slate-800">
              {selectedTier === 'green' ? `Top Third (${rankingRows.filter((_, i) => tierOf(i) === 'green').length})`
                : selectedTier === 'amber' ? `Middle Third (${rankingRows.filter((_, i) => tierOf(i) === 'amber').length})`
                : selectedTier === 'red' ? `Bottom Third (${rankingRows.filter((_, i) => tierOf(i) === 'red').length})`
                : `All Districts (${of})`}
            </span>
            <button
              onClick={() => setSelectedTier(selectedTier === 'green' ? '' : 'green')}
              className={`text-[10px] px-1.5 py-0.5 border cursor-pointer transition-all ${selectedTier === 'green' ? 'bg-emerald-500 text-white border-emerald-600' : 'bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-200'}`}
            >Top third</button>
            <button
              onClick={() => setSelectedTier(selectedTier === 'amber' ? '' : 'amber')}
              className={`text-[10px] px-1.5 py-0.5 border cursor-pointer transition-all ${selectedTier === 'amber' ? 'bg-amber-500 text-white border-amber-600' : 'bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-200'}`}
            >Middle third</button>
            <button
              onClick={() => setSelectedTier(selectedTier === 'red' ? '' : 'red')}
              className={`text-[10px] px-1.5 py-0.5 border cursor-pointer transition-all ${selectedTier === 'red' ? 'bg-rose-500 text-white border-rose-600' : 'bg-rose-100 text-rose-800 border-rose-200 hover:bg-rose-200'}`}
            >Bottom third</button>
            {selectedTier && (
              <button onClick={() => setSelectedTier('')} className="text-[10px] text-slate-600 hover:text-slate-900 underline">Clear</button>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 p-3">
            {rankingRows.map((r, idx) => {
              const tier = tierOf(idx);
              if (selectedTier && tier !== selectedTier) return null;
              return (
                <button
                  key={r.district}
                  onClick={() => goToDistrict(r.district)}
                  title={`Open ${r.district} dashboard`}
                  className={`text-left border p-3 transition-colors ${gridTone[tier]}`}
                >
                  <div className="flex items-start justify-between gap-1 mb-1">
                    <p className="text-sm font-semibold text-slate-800 truncate" title={r.district}>{r.district}</p>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 ${gridBadge[tier]}`}>#{r.rank}</span>
                  </div>
                  {selectedMetric ? (
                    <p className="text-lg font-mono font-bold text-slate-900 leading-none">{fmtVal(r.value, currentMetric?.unit)}</p>
                  ) : (
                    <>
                      <p className="text-lg font-mono font-bold text-slate-900 leading-none">Rank sum: {r.rankSum}</p>
                      <p className="text-[10px] text-slate-500 mt-1">{r.metricsCounted} of {r.totalMetrics} metrics counted</p>
                    </>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Horizontal bar chart - one bar per district, colored by tier */}
      {of > 0 && (() => {
        const filtered = rankingRows.filter((_, idx) => !selectedTier || tierOf(idx) === selectedTier);
        const maxVal = selectedMetric
          ? Math.max(...filtered.map(r => Number(r.value) || 0), 1)
          : Math.max(...filtered.map(r => r.rankSum || 0), 1);
        const barColor = { green: '#10b981', amber: '#f59e0b', red: '#f43f5e' };
        return (
          <div className="bg-white border border-slate-200">
            <div className="px-4 py-2.5 border-b border-slate-200">
              <span className="text-sm font-semibold text-slate-800">
                District Ranking Chart ({filtered.length} district{filtered.length === 1 ? '' : 's'})
              </span>
              <span className="text-[11px] text-slate-500 ml-2">
                {selectedMetric
                  ? `Bar length = ${currentMetric?.label} value`
                  : 'Bar length = sum of ranks (shorter = better)'} - click a bar to open district
              </span>
            </div>
            <div className="p-4 space-y-1.5 max-h-[700px] overflow-y-auto">
              {rankingRows.map((r, idx) => {
                const tier = tierOf(idx);
                if (selectedTier && tier !== selectedTier) return null;
                const rawVal = selectedMetric ? Number(r.value) || 0 : r.rankSum;
                const pct = Math.max(2, (rawVal / maxVal) * 100);
                const displayVal = selectedMetric ? fmtVal(r.value, currentMetric?.unit) : `Rank sum ${r.rankSum}`;
                return (
                  <div
                    key={r.district}
                    onClick={() => goToDistrict(r.district)}
                    title={`Open ${r.district} dashboard`}
                    className="flex items-center gap-2 cursor-pointer group"
                  >
                    <div className="w-8 text-right font-mono text-xs font-bold text-slate-700">#{r.rank}</div>
                    <div className="w-32 text-xs font-medium text-slate-800 truncate" title={r.district}>{r.district}</div>
                    <div className="flex-1 bg-slate-100 h-6 relative overflow-hidden">
                      <div
                        className="h-full transition-all group-hover:brightness-95"
                        style={{ width: `${pct}%`, backgroundColor: barColor[tier] }}
                      />
                      <div className="absolute inset-0 flex items-center px-2 text-[11px] font-semibold text-slate-800 pointer-events-none">
                        {displayVal}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default DistrictRanking;
